import { Component, h, State } from '@stencil/core';
import state from '@ugc-store';
import { postQuestion } from '@ugc-api/question';
import {
  handleErrorMessage,
  login,
  MIN_CHARACTERS,
  MAX_CHARACTERS,
  stringFormat,
  showToast,
  addDataLayer,
  hasHtmlTag,
  xssFilter,
} from '@ugc-utils/utils';
import { CheckboxOff, CheckboxOn } from '../../icons';

@Component({
  tag: 'ugc-qna-ask-question-modal',
  styleUrl: 'ugc-qna-ask-question-modal.scss',
  shadow: false,
})
export class UgcQnaAskQuestionModal {
  @State() formData = {
    categoryId: '',
    content: '',
  };
  @State() isSelectError = false;
  @State() isTextareaError = false;
  @State() textareaMessage = '';
  @State() isChecked = false;
  @State() isSubmitButtonDisabled: boolean = true;

  onSelectList({ detail: categoryId }) {
    this.formData = {
      ...this.formData,
      categoryId,
    };
    this.isSelectError = false;
    this.setSubmitButtonState();
  }

  onInputChange({ detail: content }) {
    this.formData = {
      ...this.formData,
      content,
    };
    this.setSubmitButtonState();

    if (!this.validateContent(content)) return;
    this.resetTextareaErrorMessage();
  }

  setSubmitButtonState() {
    const { categoryId, content } = this.formData;
    this.isSubmitButtonDisabled =
      categoryId === '' ||
      content.length < MIN_CHARACTERS ||
      content.length > MAX_CHARACTERS ||
      content.trim() === '' ||
      hasHtmlTag(content);
  }

  resetTextareaErrorMessage() {
    this.isTextareaError = false;
    this.textareaMessage = '';
  }

  validateContent(content) {
    const {
      qnaStaticLanguage: {
        IDS_UGC_QNA_TEXTAREA_LEAST_CHARACTERS,
        IDS_UGC_QNA_TEXTAREA_MORE_CHARACTERS = '',
        IDS_UGC_ENTER_PLAIN_TEXT_MESSAGE = '',
      },
    } = state;

    if (content.length < MIN_CHARACTERS) {
      this.isTextareaError = true;
      this.textareaMessage = IDS_UGC_QNA_TEXTAREA_LEAST_CHARACTERS;
      return false;
    }
    if (content.length > MAX_CHARACTERS) {
      this.isTextareaError = true;
      this.textareaMessage = stringFormat(IDS_UGC_QNA_TEXTAREA_MORE_CHARACTERS, MAX_CHARACTERS);
      return false;
    }
    if (hasHtmlTag(content)) {
      this.isTextareaError = true;
      this.textareaMessage = IDS_UGC_ENTER_PLAIN_TEXT_MESSAGE;
      return false;
    }
    return true;
  }

  async handleSubmit() {
    const {
      productInfo: { productId },
      qnaStaticLanguage: { IDS_QUESTION_SUBMITTED_SUCCESSFULLY },
      isFirstAskQuestion,
      isLogin,
    } = state;

    if (!isLogin) {
      login();
      return;
    }

    if (this.isSubmitButtonDisabled || (isFirstAskQuestion && !this.isChecked)) return;

    const { categoryId, content } = this.formData;

    // select error
    if (categoryId === '') {
      this.isSelectError = true;
      return;
    }

    // textarea error
    if (!this.validateContent(content)) return;

    this.resetTextareaErrorMessage();

    // first ask question
    if (isFirstAskQuestion && !this.isChecked) {
      return;
    }

    // request data
    const data = { productId, categoryId, content: xssFilter(content.trim()) };
    try {
      await postQuestion(data);
      this.pushDataLayer(data);
      this.resetFormData();

      state.isShowAskModal = false;
      state.isFirstAskQuestion = false;
      showToast({
        type: 'success',
        message: [IDS_QUESTION_SUBMITTED_SUCCESSFULLY],
      });
    } catch (e) {
      handleErrorMessage(e);
      this.resetFormData();
      this.pushDataLayer(data, true, e);
    }
  }

  pushDataLayer(data, isError = false, error = '') {
    const {
      productInfo: { productId, partNumber = '', displayName = '' },
      categoryList = [],
    } = state;

    const eventData = {
      event: 'form_submit',
      form_submit: {
        name: 'qna - product question',
        complete: !isError,
        type: 'dynamic',
        error: '',
        data: {
          topic: categoryList.find(item => item.id === data.categoryId).name || '',
          item_id: `${productId}`,
          item_part_number: partNumber,
          item_name: displayName,
        },
      },
    };

    if (isError) {
      eventData.form_submit = { ...eventData.form_submit, error };
    }

    addDataLayer(eventData);
  }

  handleCancel() {
    state.isShowAskModal = false;
    this.resetFormData();

    // reser modal state
    this.isSelectError = false;
    this.isTextareaError = false;
    this.textareaMessage = '';
    this.isChecked = false;
    this.isSubmitButtonDisabled = true;
  }

  resetFormData() {
    this.formData = {
      categoryId: '',
      content: '',
    };
  }

  renderNotice(text) {
    return <p class="text-start fs-12 c-666 mt-16">{text}</p>;
  }

  renderCheckboxNotice(text) {
    return (
      <div class="mt-16 flex-row-start-start gap-8" onClick={() => (this.isChecked = !this.isChecked)}>
        <span class="shrink-0">{this.isChecked ? <CheckboxOn fill="#458500" /> : <CheckboxOff fill="#ccc" />}</span>
        <p class="fs-12 c-666">{text}</p>
      </div>
    );
  }

  renderTextarea() {
    const {
      qnaStaticLanguage: {
        IDS_LBL_SELECT_CATEGORY,
        IDS_UGC_QNA_WRITE_QUESTION,
        IDS_UGC_QNA_MODAL_NOTICE,
        IDS_UGC_QNA_ANSWER_MODAL_NOTICE,
        IDS_UGC_CATEGORY,
      },
      isFirstAskQuestion,
      categoryList,
    } = state;
    const selectCategoryList = (categoryList || [])
      .map(item => ({ ...item, defaultSelected: false }))
      .filter(item => item.id !== 0);

    return (
      <div class="ask-question-form-wrap p-24">
        <ugc-select
          selectName={IDS_LBL_SELECT_CATEGORY}
          selectNameTitle={IDS_UGC_CATEGORY}
          data={selectCategoryList}
          onSelectList={this.onSelectList.bind(this)}
          value={this.formData.categoryId}
        />
        <ugc-textarea
          textareaName={IDS_UGC_QNA_WRITE_QUESTION}
          style={{ display: 'block', marginTop: '20px' }}
          height={124}
          textareaValue={this.formData.content}
          isError={this.isTextareaError}
          errorMessage={this.textareaMessage}
          onInputChange={this.onInputChange.bind(this)}
        />
        {isFirstAskQuestion
          ? this.renderCheckboxNotice(IDS_UGC_QNA_ANSWER_MODAL_NOTICE)
          : this.renderNotice(IDS_UGC_QNA_MODAL_NOTICE)}
      </div>
    );
  }

  render() {
    const {
      isLogin,
      qnaStaticLanguage: { IDS_UGC_QNA_ASK_QUESTION_MODAL_TITLE, IDS_UGC_QNA_MODAL_SUBMIT },
      isShowAskModal,
      isFirstAskQuestion,
    } = state;
    let disabled = (isLogin && this.isSubmitButtonDisabled) || (isFirstAskQuestion && !this.isChecked);

    return (
      <ugc-drawer
        isShow={isShowAskModal}
        modalTitle={IDS_UGC_QNA_ASK_QUESTION_MODAL_TITLE}
        submitButtonText={IDS_UGC_QNA_MODAL_SUBMIT}
        type="primary"
        disabled={disabled}
        handleSubmit={this.handleSubmit.bind(this)}
        handleCancel={this.handleCancel.bind(this)}
        isGray
      >
        {isShowAskModal && this.renderTextarea()}
      </ugc-drawer>
    );
  }
}
